import { types, getRoot, flow, getSnapshot } from 'mobx-state-tree'

import * as StringTools from '~/utilities/string'
import * as FileTools from '~/utilities/files'
import TRAFFIC_TICKET_VALIDATIONS from '~/utilities/trafficTicketValidations'
import {
  MAX_FILE_SIZE_MB,
  TRAFFIC_STATUS_ORDER,
  TRAFFIC_STATUS
} from '~/utilities/constants'
import TRAFFIC_CONFIG from '~/components/traffic-tickets/configuration'

import * as TrafficTicketService from '~/services/trafficTicket'
import { trackMetaBrowserLead } from '~/utilities/metaCapiBrowserEvents'

import PersistedImage from '~/stores/models/persistedImage'
import buildPersistedChangesStore from '~/stores/models/persistedChanges'
import buildCustomValidationStore from '~/stores/models/customValidation'
import TrafficQuote from './TrafficQuote'
import TrafficCourt from './TrafficCourt'
import TrafficRegion from './TrafficRegion'
import TrafficCounty from './TrafficCounty'
import Charge from './Charge'
import FetcherMovingViolation from './FetcherMovingViolation'

const TrafficTicketBase = types
  .model('TrafficTicket', {
    // Web APp Ticket Stuff

    // This is necessary for handling multiple empty tickets
    fakeId: types.optional(types.string, StringTools.random),

    persistedTicketImage: types.maybeNull(PersistedImage),
    persistedEvidenceImages: types.array(PersistedImage),
    // Not sure if we need capital 'C' charges
    charges: types.maybeNull(types.array(Charge)),
    defendant: types.optional(types.string, ''),
    // Boolean to indicate whether TrafficTicket is a DOT ticket
    isDOTTicket: types.optional(types.boolean, false),
    // Boolean to indicate whether TrafficTicket is from criminal fetcher
    isCriminalFetcher: types.optional(types.boolean, false),

    // traffic quote
    quote: types.maybeNull(types.late(() => types.reference(TrafficQuote))),

    // case protection - determined by hitting backend
    caseProtectionEligible: types.optional(types.boolean, false),

    // Actual Ticket Stuff
    objectId: types.optional(types.string, ''),
    searchResultId: types.optional(types.string, ''),
    ticketID: types.optional(types.string, ''),
    // status: types.maybeNull(types.optional(types.string, TRAFFIC_STATUS.temp)),
    status: types.maybeNull(types.string),
    trafficViolation: types.maybeNull(types.string, null),
    ticketImage: types.maybeNull(types.string),
    violationName: types.maybeNull(types.string),
    fetcherViolation: types.maybeNull(FetcherMovingViolation),
    locationState: types.maybeNull(types.string),
    locationCourt: types.maybeNull(types.string),
    comment: types.maybeNull(types.string),
    points: types.maybeNull(types.number),
    fightable: types.maybeNull(types.boolean),
    caseProtectionActivated: types.optional(types.boolean, false),
    caseProtectionFee: types.maybeNull(types.number),
    // Prices
    winItPrice: types.maybeNull(types.number),
    basePrice: types.maybeNull(types.number),
    platformFee: types.maybeNull(types.number),
    attorneyPrice: types.maybeNull(types.number),
    // Date
    createdAt: types.maybeNull(types.Date),
    updatedAt: types.maybeNull(types.Date),
    issueDate: types.maybeNull(types.Date),
    hearingDate: types.maybeNull(types.Date),
    dateSubmitted: types.maybeNull(types.Date),
    dateApproved: types.maybeNull(types.Date),
    // Pointers
    user: types.maybeNull(types.string),
    attorney: types.maybeNull(types.string),
    trafficRegion: types.maybeNull(
      types.reference(
        TrafficRegion
        // submissionStoreUtils.ticketArrayReference('trafficRegions')
      )
    ),
    trafficCourt: types.maybeNull(
      types.safeReference(
        TrafficCourt
        // submissionStoreUtils.ticketArrayReference('trafficCourts')
      )
    ),
    trafficCounty: types.maybeNull(
      types.safeReference(
        TrafficCounty
        // submissionStoreUtils.ticketArrayReference('trafficCounties')
      )
    )
  })
  .preProcessSnapshot(snapshot => {
    if (!snapshot) return null
    // this function will run on a snapshot to format it before submitting it to the store.
    const parsedSnapshot = {}
    const keys = Object.keys(snapshot)
    keys.forEach(key => {
      const input = snapshot[key]
      switch (key) {
        case 'attorney':
        case 'trafficCourt':
        case 'trafficCounty':
        case 'trafficRegion':
        case 'user':
          parsedSnapshot[key] = null
          if (typeof input === 'string') {
            parsedSnapshot[key] = input
          }
          if (input && input.objectId) {
            parsedSnapshot[key] = input.objectId
          }
          break
        case 'ticketImage':
          if (input && input.url && input.name) {
            parsedSnapshot[key] = input && input.url ? input.url : null
            parsedSnapshot.persistedTicketImage = {}
            parsedSnapshot.persistedTicketImage.persistedPointer = {
              name: input.name,
              url: input.url
            }
          }
          break
        case 'createdAt':
        case 'updatedAt':
          parsedSnapshot[key] = input ? new Date(input) : null
          break
        case 'hearingDate':
        case 'issueDate':
        case 'dateSubmitted':
        case 'dateApproved':
          if (input && typeof input === 'string') {
            const timestamp = Date.parse(input)
            if (isNaN(timestamp) == false) {
              parsedSnapshot[key] = new Date(input)
            }
          }
          if (input && typeof input === 'number') {
            parsedSnapshot[key] = new Date(input)
          }
          if (input && input.iso) {
            parsedSnapshot[key] = new Date(input.iso)
          }
          break
        case 'violation': {
          const { points, fineAmount, insuranceIncreaseEstimate3Years, trafficCodeArticles, displayName } = input
          parsedSnapshot.fetcherViolation = {
            points,
            fineAmount,
            insuranceIncreaseEstimate3Years,
            displayName,
            trafficCodeArticles,
          }
          break
        }
        case 'attorneyPrice':
        default:
          parsedSnapshot[key] = input
          break
      }
    })

    return parsedSnapshot
  })
  .actions(self => ({
    setState(state) {
      self.changeKey('locationState', state)
    },
    setTrafficRegion(objectId) {
      self.changeKey('trafficRegion', objectId)
    },
    selectQuote(trafficQuote) {
      if (!trafficQuote || !trafficQuote.objectId) return false
      self.selectAttorney(trafficQuote)
      self.changeKey('winItPrice', trafficQuote.pricing.winItPrice)
      self.changeKey('basePrice', trafficQuote.pricing.basePrice)
      const platformFee =
        trafficQuote.pricing.winItPrice - trafficQuote.pricing.attorneyPrice
      self.changeKey('platformFee', platformFee)
      self.changeKey('attorneyPrice', trafficQuote.pricing.attorneyPrice)
      return true
    },
    selectAttorney(trafficQuote) {
      if (!trafficQuote || !trafficQuote.id) return false
      self.quote = trafficQuote.id
      self.changeKey('attorney', trafficQuote.objectId)
      return true
    },
    selectCourt(courtObjectId, countyObjectId) {
      let countyToSave = countyObjectId
      if (!countyObjectId) {
        const { trafficSubmission } = getRoot(self)
        const foundCounty = trafficSubmission.trafficCounties.find(county =>
          county.trafficCourts.find(court => court.objectId === courtObjectId)
        )
        countyToSave = (foundCounty && foundCounty.objectId) || null
      }
      self.changeKey('trafficCourt', courtObjectId)
      self.changeKey('trafficCounty', countyToSave)
    },
    selectCounty(countyObjectId) {
      self.changeKey('trafficCounty', countyObjectId)
      self.changeKey('trafficCourt', null)
    },
    /**
     * Removes a local evidence image
     * @param {string} key
     */
    removeLocalEvidenceImage(id) {
      self.persistedEvidenceImages = self.persistedEvidenceImages.filter(
        item => item.id !== id
      )
    },
    /**
     * Sets status to desired status ONLY if it is above currently set status
     */
    setStatus(desiredStatus) {
      const status = self.status || 'temp'
      // If the status doesn't exist. do not set it
      if (!TRAFFIC_STATUS[desiredStatus]) return false
      // if the desired status is greater than/equal to current status, then set it
      if (TRAFFIC_STATUS_ORDER[status] <= TRAFFIC_STATUS_ORDER[desiredStatus]) {
        self.changeKey('status', desiredStatus)
        return true
      }
      return false
    },
    cleanLocalEvidenceImages() {
      const snapshot = getSnapshot(self)

      const currentTicketImage = snapshot.persistedTicketImage
        ? snapshot.persistedTicketImage.base64EncodedImage
        : null

      const seen = new Set()
      snapshot.persistedEvidenceImages.forEach(image => {
        if (
          seen.has(image.base64EncodedImage) ||
          currentTicketImage === image.base64EncodedImage
        ) {
          self.removeLocalEvidenceImage(image.id)
          if (
            currentTicketImage === image.base64EncodedImage &&
            process.browser
          ) {
            // eslint-disable-next-line
            alert(
              'You tried to add your ticket picture as evidence. It will now be removed from evidence.'
            )
          }
        } else {
          seen.add(image.base64EncodedImage)
        }
      })
    },
    handleTicketImageDrop: flow(function* ticketImageDropGen(acceptedFiles) {
      if (!acceptedFiles || !acceptedFiles.length) return false
      const validFiles = FileTools.removeLargeFilesFromArray(
        acceptedFiles,
        MAX_FILE_SIZE_MB
      )

      if (validFiles && validFiles.length) {
        const file = validFiles[0]
        self.changeKey('persistedTicketImage', {})
        yield self.persistedTicketImage.attachImageFromFile(file)
      }
      self.cleanLocalEvidenceImages()
      return true
    }),
    handleEvidenceImageDrop: flow(function* imageDropGen(acceptedFiles) {
      if (!acceptedFiles || !acceptedFiles.length) return false
      const snapshot = getSnapshot(self)

      // Prevent adding more than the maximum # of images.
      const ADDITIONAL_IMAGES_ALLOWED =
        TRAFFIC_CONFIG.MAX_NUM_EVIDENCE -
        snapshot.persistedEvidenceImages.length
      if (ADDITIONAL_IMAGES_ALLOWED <= 0) return false

      let additionalImages
      if (acceptedFiles.length > ADDITIONAL_IMAGES_ALLOWED) {
        additionalImages = acceptedFiles.slice(0, ADDITIONAL_IMAGES_ALLOWED)
      } else {
        additionalImages = acceptedFiles
      }

      // remove large files
      const validFiles = FileTools.removeLargeFilesFromArray(
        additionalImages,
        MAX_FILE_SIZE_MB
      )

      // Loop through the files to add them evidence store
      for (let x = 0; x < validFiles.length; x += 1) {
        const file = validFiles[x]
        self.persistedEvidenceImages.push({})
        const EVIDENCE_IMAGE_COUNT = self.persistedEvidenceImages.length
        const currentPersistedImage =
          self.persistedEvidenceImages[EVIDENCE_IMAGE_COUNT - 1]
        yield currentPersistedImage.attachImageFromFile(file)
      }
      self.cleanLocalEvidenceImages()
      return true
    }),
    updateTicketData(ticketData) {
      if (ticketData && ticketData.objectId) {
        self.objectId = ticketData.objectId
      }
      if (ticketData && ticketData.status) {
        self.setStatus(ticketData.status)
      }
    },
    updateTicket: flow(function* updateTicketGen(keys, sessionToken) {
      const data = self.buildDataUpdateObject(keys)
      // if data is false nothing has changed, consider it a successful save
      if (!data) {
        return true
      }
      const result = yield TrafficTicketService.update(
        self.objectId,
        data,
        sessionToken
      )
      self.updateTicketData(result)
      self.setLastUpdate(result)
      return result
    }),
    createTicket: flow(function* createTicketGen(keys, origin, fetcherName) {
      const data = self.buildDataObject(keys)
      data.telemetry = {
        origin_create: `v2 web app${origin ? ` - ${origin}` : ''}`,
        created_source: 'regular_flow',
        lookup_page: origin || 'manual_entry',
        ...(fetcherName && { fetcher_name: fetcherName }),
      }
      const result = yield TrafficTicketService.create(data)
      self.updateTicketData(result)
      self.setLastUpdate()
      if (result?.objectId) trackMetaBrowserLead(result.objectId)
      return result
    }),
    createTicketFromSearchResult: flow(function* sebtest(keys, origin, fetcherName) {
      const data = self.buildDataObject(keys)
      data.telemetry = {
        origin_create: `v2 web app${origin ? ` - ${origin}` : ''}`,
        created_source: 'regular_flow',
        lookup_page: origin || 'traffic-lookup',
        ...(fetcherName && { fetcher_name: fetcherName }),
      }
      data.attorneyId = data.attorney
      delete data.attorney // Replacing attorney key by attorneyId for the backend (better understanding)
      const result = yield TrafficTicketService.createTrafficTicketFromSearchResult(data)
      self.updateTicketData(result)
      self.setLastUpdate()
      if (result?.objectId) trackMetaBrowserLead(result.objectId)
      return result
    }),
    createTrafficTicketFromDOTTicket: flow(function* createTrafficTicketFromDOTTicketGen(keys, sessionToken, origin, fetcherName) {
      const data = self.buildDataObject(keys)
      data.telemetry = {
        origin_create: `v2 web app${origin ? ` - ${origin}` : ''}`,
        created_source: 'regular_flow',
        lookup_page: origin || 'dot',
        ...(fetcherName && { fetcher_name: fetcherName }),
      }
      data.objectId = self.objectId
      const result = yield TrafficTicketService.createTrafficTicketFromDOTTicket(data, sessionToken)
      self.updateTicketData(result)
      self.changeKey('isDOTTicket', false)
      self.setLastUpdate()
      if (result?.objectId) trackMetaBrowserLead(result.objectId)
      return result
    }),
    uploadTicketImage: flow(function* uploadTicketImageGen() {
      if (!self.isPersisted) {
        return false
      }
      // if we have no image return false
      if (!self.persistedTicketImage) return false
      // if the image has not changed, it has an image, and has been uploaded return false
      if (
        !self.hasKeyChanged('persistedTicketImage') &&
        self.persistedTicketImage.isImageAttached &&
        self.persistedTicketImage.isPersisted
      ) {
        return false
      }
      const savedImagePointer = yield self.persistedTicketImage.uploadImage()
      yield TrafficTicketService.addImageToTicket(
        savedImagePointer,
        self.objectId
      )
      self.setLastUpdateKey('persistedTicketImage', self.persistedTicketImage)
      return true
    }),
    uploadEvidenceImage: flow(function* uploadEvidenceImageGen(
      evidenceImageObject,
      userObjectId
    ) {

      if (!evidenceImageObject || !evidenceImageObject.isImageAttached) {
        return false
      }
      if (evidenceImageObject.isPersisted) {

        return true
      }
      const savedImagePointer = yield evidenceImageObject.uploadImage()
      const finalTicket = yield TrafficTicketService.createEvidenceImageEntry(
        {
          name: savedImagePointer.name,
          url: savedImagePointer.url
        },
        self.objectId,
        userObjectId
      )
      return true
    }),
    uploadAllEvidenceImages: flow(function* uploadEvidenceImageGen(
      userObjectId
    ) {
      const updates = []
      for (let x = 0; x < self.persistedEvidenceImages.length; x += 1) {
        const evidenceImage = self.persistedEvidenceImages[x]
        const result = yield self.uploadEvidenceImage(
          evidenceImage,
          userObjectId
        )
        updates.push(result)
      }
      return updates
    }),
    checkAndFetchEvidenceImages: flow(function* checkAndFetchGen() {
      try {
        if (!self.objectId) return false
        const images = yield TrafficTicketService.getTicketEvidenceImages(
          self.objectId
        )
        if (!images || !images.length) return false
        self.persistedEvidenceImages = images.map(image => ({
          persistedPointer: image
        }))
        const results = yield Promise.all(
          self.persistedEvidenceImages.map(image =>
            image.fetchAlreadyPersistedImage()
          )
        )
        return results
      } catch (err) {
        console.error('Error loading already uploaded evidence images', err)
        return false
      }
    }),
    toggleCaseProtection() {
      if (self.caseProtectionEligible) {
        self.caseProtectionActivated = !self.caseProtectionActivated
      }
    },
    fetchCaseProtection: flow(function* fetchCaseProtectionGen(sessionToken) {
      try {
        if (!self.objectId) return false
        const response = yield TrafficTicketService.getCaseProtectionEligible(
          self.objectId,
          sessionToken
        )
        if (response.eligible && response.fee >= 0) {
          self.caseProtectionEligible = true
          self.caseProtectionActivated = false
          self.caseProtectionFee = response.fee
        } else {
          self.caseProtectionEligible = false
          self.caseProtectionActivated = false
          self.caseProtectionFee = null
        }
      } catch (error) {
        console.error('Error getting case protection information', error)
      }
    }),
    setDefendant(defendantName) {
      self.defendant = defendantName
    }
  }))
  .views(self => ({
    get totalPrice() {
      let displayPrice = 0
      if (self.winItPrice) displayPrice += self.winItPrice
      if (self.caseProtectionActivated) {
        if (self.caseProtectionFee) displayPrice += self.caseProtectionFee
      }
      return displayPrice
    },
    get formattedViolationName() {
      return self.violationName ? self.violationName.charAt(0).toUpperCase() + self.violationName.toLowerCase().slice(1) : ''
    },
  }))

const PersistedChangesStore = buildPersistedChangesStore({
  keyTransformer: (self, key) => {
    switch (key) {
      case 'trafficCourt':
      case 'trafficCounty':
      case 'trafficRegion':
        if (self[key] && self[key].objectId) {
          return self[key].objectId
        }
        return self[key]
      default:
        return self[key]
    }
  }
})

const CustomValidationStore = buildCustomValidationStore({
  validationObject: TRAFFIC_TICKET_VALIDATIONS
})

const TrafficTicket = types.compose(
  TrafficTicketBase,
  PersistedChangesStore,
  CustomValidationStore
)
export default TrafficTicket
